/**
 * @fileoverview Modal de criação guiada de Board (CRM Kanban).
 *
 * Apresenta dois templates pré-definidos (Gestão de Leads / Gestão de Tarefas)
 * como cards selecionáveis e coleta nome, cor e senha de confirmação. A criação
 * real (POST /crm-boards) é responsabilidade do chamador — este modal apenas
 * devolve `{ name, color, password, template }` via `onConfirm`.
 *
 * Visual: padrão SaaS — discreto, monocromático, com cor apenas para o estado
 * selecionado, foco e botão primário (sem chips coloridos por coluna).
 */

import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import { X, Check, TrendingUp, ListChecks, Lock } from "lucide-react";

import {
  BOARD_TEMPLATES,
  leadManagementTemplate
} from "../../constants/boardTemplates";

const BOARD_COLORS = [
  "#5B7CFF", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#f97316", "#14b8a6", "#6366f1"
];

const TEMPLATE_ICONS = {
  leads: TrendingUp,
  tasks: ListChecks
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = isDark ? "#5B7CFF" : "#3D5CFD";
  const accentSoft = isDark ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)";

  return {
    dialogPaper: {
      "& .MuiDialog-paper": {
        borderRadius: 16,
        background: isDark ? "#0E0F12" : "#FFFFFF",
        border: isDark
          ? "1px solid rgba(255,255,255,0.06)"
          : "1px solid rgba(15,23,42,0.06)",
        boxShadow: isDark
          ? "0 24px 64px -16px rgba(0,0,0,0.6), 0 8px 24px -8px rgba(0,0,0,0.4)"
          : "0 24px 64px -16px rgba(15,23,42,0.18), 0 8px 24px -8px rgba(15,23,42,0.10)",
        maxWidth: 580,
        width: "100%",
        margin: 16
      }
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      padding: theme.spacing(3, 3, 0, 3)
    },
    headerText: {
      minWidth: 0,
      flex: 1
    },
    title: {
      fontSize: "1.15rem",
      fontWeight: 600,
      lineHeight: 1.25,
      color: isDark ? "#FFFFFF" : "#0F172A",
      margin: 0,
      letterSpacing: "-0.01em"
    },
    subtitle: {
      marginTop: 6,
      fontSize: 13,
      color: isDark ? "rgba(255,255,255,0.55)" : "#64748B",
      lineHeight: 1.5
    },
    closeBtn: {
      flexShrink: 0,
      width: 32,
      height: 32,
      borderRadius: 10,
      color: isDark ? "rgba(255,255,255,0.55)" : "#64748B",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
        color: isDark ? "#FFFFFF" : "#0F172A"
      }
    },
    body: {
      padding: theme.spacing(2.5, 3, 0, 3)
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: isDark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      marginBottom: 10
    },
    templateGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr"
      }
    },
    templateCard: {
      position: "relative",
      textAlign: "left",
      display: "flex",
      flexDirection: "column",
      padding: 14,
      borderRadius: 12,
      cursor: "pointer",
      background: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(15,23,42,0.10)",
      transition: "border-color .15s ease, background .15s ease",
      outline: "none",
      "&:hover": {
        borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.20)",
        background: isDark ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.015)"
      },
      "&:focus-visible": {
        borderColor: accent,
        boxShadow: `0 0 0 3px ${accentSoft}`
      }
    },
    templateCardSelected: {
      borderColor: accent,
      background: accentSoft,
      "&:hover": {
        borderColor: accent,
        background: accentSoft
      }
    },
    cardHead: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 8
    },
    cardIconWrap: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      color: isDark ? "rgba(255,255,255,0.8)" : "#475569",
      flexShrink: 0
    },
    cardIconWrapSelected: {
      background: accent,
      color: "#FFFFFF"
    },
    cardTitle: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.2,
      color: isDark ? "#FFFFFF" : "#0F172A",
      letterSpacing: "-0.005em"
    },
    cardDescription: {
      fontSize: 12.5,
      lineHeight: 1.5,
      color: isDark ? "rgba(255,255,255,0.55)" : "#64748B",
      margin: 0
    },
    columnsLine: {
      marginTop: 10,
      paddingTop: 10,
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      fontSize: 11.5,
      lineHeight: 1.55,
      color: isDark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      wordBreak: "break-word"
    },
    checkBadge: {
      position: "absolute",
      top: 12,
      right: 12,
      width: 18,
      height: 18,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: accent,
      color: "#FFFFFF"
    },
    fields: {
      marginTop: 18,
      display: "flex",
      flexDirection: "column",
      gap: 14
    },
    fieldLabel: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: isDark ? "rgba(255,255,255,0.5)" : "#475569",
      marginBottom: 7,
      display: "flex",
      alignItems: "center",
      gap: 5
    },
    input: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        background: isDark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
        "& fieldset": {
          borderColor: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.12)"
        },
        "&:hover fieldset": {
          borderColor: isDark ? "rgba(255,255,255,0.20)" : "rgba(15,23,42,0.22)"
        },
        "&.Mui-focused fieldset": {
          borderColor: accent,
          borderWidth: 1.5
        }
      },
      "& .MuiOutlinedInput-input": {
        fontSize: 13.5,
        fontWeight: 500,
        color: isDark ? "#FFFFFF" : "#0F172A",
        padding: "11px 12px"
      }
    },
    colorPicker: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap"
    },
    colorDot: {
      width: 24,
      height: 24,
      borderRadius: "50%",
      cursor: "pointer",
      border: "2px solid transparent",
      transition: "transform .12s ease",
      "&:hover": { transform: "scale(1.10)" }
    },
    colorDotSelected: {
      border: isDark ? "2px solid #0E0F12" : "2px solid #FFFFFF",
      boxShadow: isDark
        ? "0 0 0 2px rgba(255,255,255,0.7)"
        : "0 0 0 2px #0F172A"
    },
    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
      padding: theme.spacing(2.5, 3, 3, 3),
      marginTop: theme.spacing(1)
    },
    cancelBtn: {
      padding: "9px 16px",
      borderRadius: 10,
      fontSize: 13,
      fontWeight: 600,
      border: "none",
      cursor: "pointer",
      background: "transparent",
      color: isDark ? "rgba(255,255,255,0.7)" : "#475569",
      transition: "background .15s ease",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)"
      }
    },
    confirmBtn: {
      padding: "9px 18px",
      borderRadius: 10,
      fontSize: 13,
      fontWeight: 600,
      border: "none",
      cursor: "pointer",
      background: accent,
      color: "#FFFFFF",
      transition: "filter .12s ease, opacity .12s ease",
      "&:hover:not(:disabled)": {
        filter: "brightness(1.08)"
      },
      "&:disabled": {
        opacity: 0.45,
        cursor: "not-allowed"
      }
    }
  };
});

const BoardTemplateModal = ({ open, onClose, onConfirm }) => {
  const classes = useStyles();

  const [selectedKey, setSelectedKey] = useState(leadManagementTemplate.key);
  const [boardName, setBoardName] = useState(leadManagementTemplate.defaultBoardName);
  const [boardColor, setBoardColor] = useState(leadManagementTemplate.defaultColor);
  const [boardPassword, setBoardPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);

  const selectedTemplate = useMemo(
    () => BOARD_TEMPLATES.find((t) => t.key === selectedKey) || BOARD_TEMPLATES[0],
    [selectedKey]
  );

  useEffect(() => {
    if (open) {
      setSelectedKey(leadManagementTemplate.key);
      setBoardName(leadManagementTemplate.defaultBoardName);
      setBoardColor(leadManagementTemplate.defaultColor);
      setBoardPassword("");
      setSubmitting(false);
      setNameTouched(false);
    }
  }, [open]);

  const handleSelectTemplate = (tpl) => {
    setSelectedKey(tpl.key);
    if (!nameTouched) {
      setBoardName(tpl.defaultBoardName);
    }
  };

  const canSubmit =
    !!selectedKey &&
    boardName.trim().length > 0 &&
    boardPassword.length > 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({
        name: boardName.trim(),
        color: boardColor,
        password: boardPassword,
        template: selectedKey
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialogPaper}
      maxWidth={false}
      onKeyDown={handleKeyDown}
    >
      <div className={classes.header}>
        <div className={classes.headerText}>
          <h2 className={classes.title}>Como você quer começar?</h2>
          <p className={classes.subtitle}>
            Escolha um modelo. As colunas vêm prontas e podem ser ajustadas depois.
          </p>
        </div>
        <IconButton className={classes.closeBtn} onClick={onClose} aria-label="Fechar">
          <X size={16} />
        </IconButton>
      </div>

      <div className={classes.body}>
        <div className={classes.sectionLabel}>Modelo</div>
        <div className={classes.templateGrid}>
          {BOARD_TEMPLATES.map((tpl) => {
            const Icon = TEMPLATE_ICONS[tpl.key] || ListChecks;
            const isSelected = tpl.key === selectedKey;
            return (
              <div
                key={tpl.key}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => handleSelectTemplate(tpl)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectTemplate(tpl);
                  }
                }}
                className={`${classes.templateCard} ${isSelected ? classes.templateCardSelected : ""}`}
              >
                {isSelected && (
                  <div className={classes.checkBadge}>
                    <Check size={11} strokeWidth={3} />
                  </div>
                )}
                <div className={classes.cardHead}>
                  <div
                    className={`${classes.cardIconWrap} ${isSelected ? classes.cardIconWrapSelected : ""}`}
                  >
                    <Icon size={16} strokeWidth={2} />
                  </div>
                  <div className={classes.cardTitle}>{tpl.title}</div>
                </div>
                <p className={classes.cardDescription}>{tpl.description}</p>
                <div className={classes.columnsLine}>{tpl.columns.join(" · ")}</div>
              </div>
            );
          })}
        </div>

        <div className={classes.fields}>
          <div>
            <div className={classes.fieldLabel}>Nome do quadro</div>
            <TextField
              autoFocus
              value={boardName}
              onChange={(e) => {
                setBoardName(e.target.value);
                setNameTouched(true);
              }}
              placeholder={selectedTemplate.defaultBoardName}
              fullWidth
              variant="outlined"
              size="small"
              className={classes.input}
            />
          </div>

          <div>
            <div className={classes.fieldLabel}>Cor de identificação</div>
            <div className={classes.colorPicker}>
              {BOARD_COLORS.map((c) => (
                <div
                  key={c}
                  role="button"
                  tabIndex={0}
                  aria-label={`Cor ${c}`}
                  aria-pressed={boardColor === c}
                  onClick={() => setBoardColor(c)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setBoardColor(c);
                    }
                  }}
                  className={`${classes.colorDot} ${boardColor === c ? classes.colorDotSelected : ""}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>

          <div>
            <div className={classes.fieldLabel}>
              <Lock size={11} strokeWidth={2.2} />
              Confirme sua senha
            </div>
            <TextField
              type="password"
              value={boardPassword}
              onChange={(e) => setBoardPassword(e.target.value)}
              placeholder="Senha do usuário"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.input}
              autoComplete="new-password"
              inputProps={{ autoComplete: "new-password" }}
            />
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <button type="button" className={classes.cancelBtn} onClick={onClose}>
          Cancelar
        </button>
        <button
          type="button"
          className={classes.confirmBtn}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? "Criando..." : "Criar quadro"}
        </button>
      </div>
    </Dialog>
  );
};

export default BoardTemplateModal;
